import { useEffect, useMemo, useRef, useState } from "react";
import type { LocPick } from "./LocationField.js";

const TILE_Z = 14;

function lonLatToTile(lon: number, lat: number, z: number) {
  const n = Math.pow(2, z);
  const x = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

type Status = "idle" | "loading" | "hit" | "miss" | "error";

/** Mini map beside the notes (real OSM/CARTO tiles + Nominatim geocode).
 *  Renders a stylized grid placeholder when offline or unresolved. */
export function MiniMap({ label, pinColor, knownCoords }: {
  label: string;
  pinColor: string;
  knownCoords: LocPick | null;
}) {
  const [coords, setCoords] = useState<LocPick | null>(knownCoords);
  const [status, setStatus] = useState<Status>(knownCoords ? "hit" : "idle");
  const [dims, setDims] = useState({ W: 156, H: 132 });
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Track container size so tiles stay centered when the grid stretches it.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        if (width > 0 && height > 0) setDims({ W: width, H: height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // If the autocomplete pick already resolved coords, use them.
    if (knownCoords) {
      setCoords(knownCoords);
      setStatus("hit");
      return;
    }
    if (!label) { setCoords(null); setStatus("idle"); return; }
    const ctl = new AbortController();
    setStatus("loading");
    const tmr = setTimeout(async () => {
      try {
        const r = await fetch(
          "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" + encodeURIComponent(label),
          { signal: ctl.signal, headers: { "Accept-Language": "en" } },
        );
        const data = (await r.json()) as Array<{ lat: string; lon: string }>;
        if (data && data[0]) {
          setCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
          setStatus("hit");
        } else {
          setCoords(null);
          setStatus("miss");
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") { setCoords(null); setStatus("error"); }
      }
    }, 550);
    return () => { clearTimeout(tmr); ctl.abort(); };
  }, [label, knownCoords]);

  // 3x3 tile grid centered on the location so the pin sits dead center.
  const tiles = useMemo(() => {
    if (!coords) return null;
    const { W, H } = dims;
    const cx = W / 2, cy = H / 2;
    const exact = lonLatToTile(coords.lon, coords.lat, TILE_Z);
    const xTile = Math.floor(exact.x), yTile = Math.floor(exact.y);
    const dx = (exact.x - xTile) * 256;
    const dy = (exact.y - yTile) * 256;
    const out: Array<{ x: number; y: number; left: number; top: number }> = [];
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        out.push({ x: xTile + i, y: yTile + j, left: cx - dx + i * 256, top: cy - dy + j * 256 });
      }
    }
    return out;
  }, [coords, dims]);

  const isDark = document.documentElement.classList.contains("dark");
  const tileStyle = isDark ? "dark_all" : "light_all";

  return (
    <div ref={containerRef} className="mini-map" style={{ ["--pin" as string]: pinColor }}>
      {tiles ? (
        <div className="map-tiles">
          {tiles.map((t) => {
            const sub = ["a", "b", "c"][(t.x + t.y) % 3];
            return (
              <img
                key={`${t.x}-${t.y}`}
                className="map-tile"
                src={`https://${sub}.basemaps.cartocdn.com/${tileStyle}/${TILE_Z}/${t.x}/${t.y}.png`}
                style={{ left: t.left + "px", top: t.top + "px" }}
                alt=""
              />
            );
          })}
        </div>
      ) : (
        <div className="map-bg" />
      )}
      {coords && <div className="map-pin" />}
      {status === "loading" && <div className="map-loading">searching…</div>}
      {status === "miss" && <div className="map-loading">not found</div>}
      {coords && <div className="map-attrib">© OSM · CARTO</div>}
      <div className="map-label" title={label}>{label}</div>
    </div>
  );
}
